import { api, apiUpload, API_BASE } from '../lib/api';
import { auth } from '../lib/firebase';
import type { Item, ItemType } from '../types';

function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  return url;
}

export function parseDates(item: any): Item {
  return {
    ...item,
    imageUrl: resolveImageUrl(item.imageUrl),
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
    folderIds: item.collectionIds ?? [],
    sharedWith: item.sharedWith ?? [],
    sharedWithContactIds: item.sharedWithContactIds ?? [],
    tags: item.tags ?? [],
    autoTags: item.autoTags ?? [],
    flags: item.flags ?? 0,
  };
}

export async function createItem(
  _userId: string,
  data: {
    type: ItemType;
    title: string;
    content: string;
    folderIds?: string[];
    tags?: string[];
    mimeType?: string;
    fileName?: string;
    fileSize?: number;
  },
): Promise<string> {
  const res = await api<{ id: string }>('/api/items', {
    method: 'POST',
    body: JSON.stringify({
      type: data.type,
      title: data.title,
      content: data.content,
      tags: data.tags,
      collectionIds: data.folderIds,
    }),
  });
  return res.id;
}

export async function getItem(itemId: string): Promise<Item | null> {
  try {
    const item = await api<any>(`/api/items/${itemId}`);
    return parseDates(item);
  } catch {
    return null;
  }
}

export async function getUserItems(
  _userId: string,
  maxItems = 50,
): Promise<Item[]> {
  const items = await api<any[]>(`/api/items?limit=${maxItems}`);
  return items.map(parseDates);
}


export async function getItemsByTag(
  tag: string,
  _maxItems = 50,
): Promise<Item[]> {
  const items = await api<any[]>(`/api/items/search?q=${encodeURIComponent(tag)}`);
  return items.map(parseDates);
}

export async function getFolderItems(
  folderId: string,
  _maxItems = 50,
): Promise<Item[]> {
  const col = await api<any>(`/api/collections/${folderId}`);
  return (col.items ?? []).map((i: any) => parseDates({
    ...i,
    summary: i.summary ?? '',
    tags: i.tags ?? [],
    autoTags: i.autoTags ?? [],
    visibility: i.visibility ?? 'private',
    sharedWith: i.sharedWith ?? [],
    enrichmentStatus: i.enrichmentStatus ?? 'complete',
    collectionIds: [],
    updatedAt: i.updatedAt ?? i.createdAt,
    createdBy: i.createdBy ?? '',
  }));
}

export async function getItemsSharedWithMe(
  _userId: string,
  _maxItems = 50,
): Promise<Item[]> {
  const items = await api<any[]>('/api/shared/with-me');
  return items.map(parseDates);
}

export async function updateItem(
  itemId: string,
  data: Partial<Pick<Item, 'title' | 'tags' | 'folderIds' | 'visibility' | 'sharedWith'>>,
): Promise<void> {
  const body: Record<string, unknown> = { ...data };
  if (data.folderIds !== undefined) {
    body.collectionIds = data.folderIds;
    delete body.folderIds;
  }
  await api(`/api/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function shareItem(
  itemId: string,
  recipientEmails: string[],
  senderName: string,
  title?: string,
): Promise<void> {
  const shareUrl = `${window.location.origin}/items/${itemId}`;
  await api(`/api/items/${itemId}/share`, {
    method: 'POST',
    body: JSON.stringify({ recipientEmails, senderName, title, shareUrl }),
  });
}

export async function unshareItem(
  itemId: string,
  emailToRemove: string,
  remainingEmails: string[],
): Promise<void> {
  const filtered = remainingEmails.filter((e) => e !== emailToRemove);
  await api(`/api/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify({
      sharedWith: filtered,
      visibility: filtered.length > 0 ? 'shared' : 'private',
    }),
  });
}

export async function deleteItem(itemId: string): Promise<void> {
  await api(`/api/items/${itemId}`, { method: 'DELETE' });
}

export async function refreshItem(itemId: string): Promise<void> {
  await api(`/api/items/${itemId}/enrich`, { method: 'POST' });
}

export async function getItemDebug(itemId: string): Promise<any> {
  return api<any>(`/api/items/${itemId}/debug`);
}

export async function uploadItemFile(
  _userId: string,
  itemId: string,
  file: File,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('itemId', itemId);
  const res = await apiUpload('/api/upload', formData);
  return res.url;
}

export async function updateItemFlags(itemId: string, flags: number): Promise<void> {
  await api(`/api/items/${itemId}/flags`, {
    method: 'PATCH',
    body: JSON.stringify({ flags }),
  });
}

export async function streamItemsSummary(
  itemIds: string[],
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = await auth.currentUser?.getIdToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/items/summary`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ itemIds }),
    });
  } catch (e: any) {
    if (e?.name === 'AbortError') return;
    onError(e?.message || 'Network error');
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    onError(text || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    onError('No response body');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const parsed = JSON.parse(line.slice(6));
        if (parsed.chunk) onChunk(parsed.chunk);
        if (parsed.done) { onDone(); return; }
        if (parsed.error) { onError(parsed.error); return; }
      } catch {
        // ignore malformed lines
      }
    }
  }
  onDone();
}
